// The rotating door code a staff device shows at the door (core/door.ts). Parents scan it with
// the guardian app to drop off or pick up. Generated on the device every 30 s: works offline.
import { useEffect, useState } from "react";
import { doorWindow, issueDoorCode, DOOR_WINDOW_S } from "@vavr/core";
import { QrCode } from "./qr.tsx";

/** `staffId` null with `selfService`: the unstaffed door tablet's code, which only asks for a pickup. */
export function useDoorCode(o: { key: CryptoKey | null; siteId: string | null; deviceId: string | null; staffId: string | null; selfService?: boolean; purpose?: "pickup" | "dropoff" }) {
  const [code, setCode] = useState<string | null>(null);
  const [left, setLeft] = useState(DOOR_WINDOW_S);
  useEffect(() => {
    if (!o.key || !o.siteId || !o.deviceId || (!o.staffId && !o.selfService)) { setCode(null); return; }
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    let signed = -1;
    // Sign once per 30 s window (a signature is not free, and every new string redraws the QR and
    // goes to the NFC reader); the countdown ticks every second on its own.
    const render = async () => {
      const now = Date.now() / 1000;
      const window = doorWindow(now);
      if (window !== signed) {
        const c = await issueDoorCode(o.key!, { site_id: o.siteId!, device_id: o.deviceId!, staff_id: o.staffId ?? null, window, ...(o.purpose ? { purpose: o.purpose } : {}) });
        if (!live) return;
        signed = window;
        setCode(c);
      }
      setLeft(DOOR_WINDOW_S - Math.floor(now % DOOR_WINDOW_S));
      timer = setTimeout(render, 1000);
    };
    void render();
    return () => { live = false; clearTimeout(timer); };
  }, [o.key, o.siteId, o.deviceId, o.staffId, o.selfService, o.purpose]);
  return { code, left };
}

export function DoorCodeView({ code, size, caption, quiet }: { code: string | null; size: number; caption?: string; quiet?: number }) {
  return (
    <figure className="pmu-door">
      {code ? <QrCode text={code} size={size} quiet={quiet} label="Door code: parents scan this with the Vavr app" /> : <div className="pmu-door__empty" style={{ width: size, height: size }} />}
      {caption && <figcaption className="pmu-door__caption">{caption}</figcaption>}
    </figure>
  );
}
